import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styles from '../../_css/Cost.module.css';
import { Card, KpiCard, CardTitle, TableHeader, TableCell, Badge, Bar } from '../UI';
import { DollarSign, BarChart2, Zap, TrendingUp } from 'lucide-react';
import { getCost } from '../../requests';
import { notifyError } from '../../utils/helper';
import useRole from '../../hooks/getRole';
import { parseAppQuery } from '../../utils/urlContextParams';

const emptyState = {
  summary: null,
  simulations: [],
  byBrand: [],
  monthlyTrend: [],
};

const NA = 'Not available';
const LOADING_TEXT = 'Loading...';
const STAT_LOADING = '-';

function LoadingInline() {
  return (
    <div className={styles.loadingBlock}>
      <span className={styles.loadingSpinner} aria-hidden />
      <span>{LOADING_TEXT}</span>
    </div>
  );
}

function formatRunDate(iso) {
  if (!iso) return NA;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return NA;
  }
}

function displayText(loading, v, fallback = NA) {
  if (loading) return STAT_LOADING;
  if (v == null || v === '') return fallback;
  return v;
}

function displayNumber(loading, n, format) {
  if (loading) return STAT_LOADING;
  if (n == null || Number.isNaN(Number(n))) return NA;
  return format(Number(n));
}

function displayMoney(loading, n, decimals = 2) {
  return displayNumber(loading, n, (x) => `$${x.toFixed(decimals)}`);
}

const SIMULATIONS = 5;
const BRANDS = 5;
const MONTHS = 5;

export default function SuperAdminCostView() {
  const role = useRole();
  const userInfo = useSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(emptyState);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (String(role || '').toLowerCase() !== 'super_admin') {
      navigate('/', { replace: true });
    }
  }, [role, navigate]);

  useEffect(() => {
    if (String(role || '').toLowerCase() !== 'super_admin') return undefined;

    if (userInfo?.agencyId) {
      const q = parseAppQuery(location.search);
      if (q.agency_id == null || q.agency_id === '') {
        navigate(`/cost?agency_id=${userInfo.agencyId}`, { replace: true });
        return undefined;
      }
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await getCost({
          simulations: SIMULATIONS,
          brand: BRANDS,
          months: MONTHS,
        });
        if (cancelled) return;
        const payload = response?.data;
        setData(
          payload && typeof payload === 'object'
            ? {
                summary: payload.summary ?? null,
                simulations: Array.isArray(payload.simulations) ? payload.simulations : [],
                byBrand: Array.isArray(payload.byBrand) ? payload.byBrand : [],
                monthlyTrend: Array.isArray(payload.monthlyTrend) ? payload.monthlyTrend : [],
              }
            : emptyState,
        );
      } catch (error) {
        if (!cancelled) {
          setData(emptyState);
          notifyError(error?.response?.data?.message || 'Failed to fetch cost');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [role, userInfo?.agencyId, location.search, navigate]);

  const summary = data.summary;
  const simulations = data.simulations;
  const byBrand = data.byBrand;
  const monthlyTrend = data.monthlyTrend;

  const totalCostUsdRaw = summary?.totalCostUsd;
  const totalTokensRaw = summary?.totalTokens;
  const totalRunsRaw = summary?.totalRuns;
  let avgCostRaw = summary?.avgCostPerRunUsd;
  if (
    avgCostRaw == null &&
    summary &&
    totalRunsRaw != null &&
    Number(totalRunsRaw) > 0 &&
    totalCostUsdRaw != null &&
    !Number.isNaN(Number(totalCostUsdRaw))
  ) {
    avgCostRaw = Number(totalCostUsdRaw) / Number(totalRunsRaw);
  }
  const modelSub =
    loading ? STAT_LOADING : !summary?.modelLabel?.trim() ? NA : summary.modelLabel;

  const maxBrandCost = useMemo(
    () => Math.max(0, ...byBrand.map((b) => Number(b.costUsd) || 0)),
    [byBrand],
  );

  const brandRows = useMemo(
    () => [...byBrand].sort((a, b) => (Number(b.costUsd) || 0) - (Number(a.costUsd) || 0)),
    [byBrand],
  );

  const tableTotals = useMemo(
    () =>
      simulations.reduce(
        (acc, r) => ({
          runs: acc.runs + (Number(r.runs) || 0),
          queries: acc.queries + (Number(r.queries) || 0),
          tokens: acc.tokens + (Number(r.tokens) || 0),
          costUsd: acc.costUsd + (Number(r.costUsd) || 0),
        }),
        { runs: 0, queries: 0, tokens: 0, costUsd: 0 },
      ),
    [simulations],
  );

  if (String(role || '').toLowerCase() !== 'super_admin') {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1 className={styles.title}>Cost & Tokens</h1>
        <p className={styles.subtitle}>API usage and cost overview across all simulations</p>
      </header>

      <div className={styles.kpiGrid}>
        <KpiCard
          label="Total Cost"
          value={displayMoney(loading, totalCostUsdRaw, 2)}
          sub="All simulations"
          icon={<DollarSign size={24} />}
        />
        <KpiCard
          label="Total Tokens"
          value={displayNumber(loading, totalTokensRaw, (x) => x.toLocaleString())}
          sub="Prompt + Completion"
          icon={<BarChart2 size={24} />}
        />
        <KpiCard
          label="Total Runs"
          value={displayNumber(loading, totalRunsRaw, (x) => String(x))}
          sub="Across all simulations"
          icon={<Zap size={24} />}
        />
        <KpiCard
          label="Avg Cost per Run"
          value={displayMoney(loading, avgCostRaw, 3)}
          sub={modelSub}
          icon={<TrendingUp size={24} />}
        />
      </div>

      <Card>
        <CardTitle sub="Cost breakdown per simulation">Simulation Costs</CardTitle>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <TableHeader columns={['Simulation', 'Brand', 'Last Run', 'Runs', 'Queries', 'Tokens', 'Cost', 'Status']} />
            </thead>
            <tbody>
              {loading ? (
                <tr className={styles.tableRow}>
                  <td colSpan={8} style={{ padding: 0 }}>
                    <LoadingInline />
                  </td>
                </tr>
              ) : simulations.length === 0 ? (
                <tr className={styles.tableRow}>
                  <td colSpan={8} className={styles.tableFootCell} style={{ textAlign: 'center' }}>
                    {NA}
                  </td>
                </tr>
              ) : (
                simulations.map((s, i) => (
                  <tr key={s.simulationId ?? `sim-${i}`} className={styles.tableRow}>
                    <TableCell bold>{displayText(false, s.simulationName)}</TableCell>
                    <TableCell>{displayText(false, s.brandName)}</TableCell>
                    <TableCell>{formatRunDate(s.lastRunAt)}</TableCell>
                    <TableCell>{displayNumber(false, s.runs, (x) => String(x))}</TableCell>
                    <TableCell>{displayNumber(false, s.queries, (x) => String(x))}</TableCell>
                    <TableCell>{displayNumber(false, s.tokens, (x) => x.toLocaleString())}</TableCell>
                    <TableCell bold>{displayMoney(false, s.costUsd, 3)}</TableCell>
                    <TableCell>
                      {s.status ? (
                        <Badge color="#22c55e">{s.status}</Badge>
                      ) : (
                        NA
                      )}
                    </TableCell>
                  </tr>
                ))
              )}
            </tbody>
            {!loading && simulations.length > 0 && (
              <tfoot>
                <tr className={styles.tableFootRow}>
                  <td colSpan={3} className={styles.tableFootCell}>
                    Total
                  </td>
                  <td className={styles.tableFootCell}>{tableTotals.runs}</td>
                  <td className={styles.tableFootCell}>{tableTotals.queries}</td>
                  <td className={styles.tableFootCell}>{tableTotals.tokens.toLocaleString()}</td>
                  <td className={styles.tableFootCell}>${tableTotals.costUsd.toFixed(3)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      <div className={styles.twoColGrid}>
        <Card>
          <CardTitle>Cost by Brand</CardTitle>
          <div className={styles.brandList}>
            {loading ? (
              <LoadingInline />
            ) : brandRows.length === 0 ? (
              <div style={{ color: 'var(--muted, #6b7280)', padding: '8px 0' }}>{NA}</div>
            ) : (
              brandRows.map((b, i) => (
                <div key={b.brandId ?? `brand-${i}`} className={styles.brandRow}>
                  <span className={styles.brandLabel}>{displayText(false, b.brandName)}</span>
                  <div className={styles.brandBarWrap}>
                    <Bar
                      pct={maxBrandCost > 0 ? ((Number(b.costUsd) || 0) / maxBrandCost) * 100 : 0}
                      color="#ef4444"
                      showLabel={false}
                      height={8}
                    />
                  </div>
                  <span className={styles.brandCost}>{displayMoney(false, b.costUsd, 3)}</span>
                </div>
              ))
            )}
          </div>
        </Card>
        <Card>
          <CardTitle>Monthly Trend</CardTitle>
          <div className={styles.trendList}>
            {loading ? (
              <LoadingInline />
            ) : monthlyTrend.length === 0 ? (
              <div style={{ color: 'var(--muted, #6b7280)', padding: '8px 0' }}>{NA}</div>
            ) : (
              monthlyTrend.map((m, i) => (
                <div
                  key={m.yearMonth || i}
                  className={i < monthlyTrend.length - 1 ? styles.trendRow : styles.trendRowLast}
                >
                  <div>
                    <div className={styles.trendMonth}>{displayText(false, m.label)}</div>
                    <div className={styles.trendRuns}>
                      {displayNumber(false, m.runs, (x) => `${x} runs`)}
                    </div>
                  </div>
                  <span className={styles.trendCost}>{displayMoney(false, m.costUsd, 3)}</span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
